import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  FileText,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { AccountModal } from '@/components/travel/accounting/account-modal';
import { ChartDeleteOptions } from '@/components/accounting/chart-delete-options';
import { usePermission } from '@/components/guards/permission-guard';

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('accounting');
  const { t: tCommon } = useTranslation('common');
  const canDelete = usePermission('ACCOUNTING', 'DELETE');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [cascadeDelete, setCascadeDelete] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);

  const { data: account, isLoading } = trpc.account.byId.useQuery(id!, {
    enabled: !!id,
  });

  const { data: ledgerData, isLoading: isLedgerLoading } = trpc.account.getLedger.useQuery(
    {
      accountId: id!,
      page: ledgerPage,
    },
    { enabled: !!id }
  );

  // Preparation mode adds cascade + merge to the delete dialog and lets the
  // dialog be opened for accounts the locked chart would never delete.
  const { data: chartPrep } = trpc.account.chartPrepStatus.useQuery();
  const unlocked = chartPrep?.enabled === true;
  const { data: mergeCandidates } = trpc.account.list.useQuery(
    { limit: 500 },
    { enabled: unlocked && isDeleteDialogOpen }
  );

  const utils = trpc.useUtils();

  const updateMutation = trpc.account.update.useMutation({
    onSuccess: () => {
      utils.account.byId.invalidate(id);
      utils.account.list.invalidate();
    },
  });

  // Prefer history-based back navigation so the user lands on the exact
  // tab + scroll position they came from. Falls back to the accounts tab
  // when there is no history (e.g. opening the detail via a direct URL).
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/travel/accounting?tab=accounts');
    }
  };

  const deleteMutation = trpc.account.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.account.invalidate(),
        utils.accountWorkbench.health.invalidate(),
      ]);
      goBack();
    },
  });

  const handleToggleActive = () => {
    if (account) {
      updateMutation.mutate({
        id: account.id,
        isActive: !account.isActive,
      });
    }
  };

  const handleDelete = () => {
    if (!account) return;
    // Preparation mode carries the cascade / merge choices; the locked chart
    // still sends the bare id it always sent.
    deleteMutation.mutate(
      unlocked
        ? {
            id: account.id,
            cascade: cascadeDelete,
            ...(mergeTargetId ? { reassignToAccountId: mergeTargetId } : {}),
          }
        : account.id
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-muted-foreground text-lg font-medium">{t('accountNotFound')}</p>
        <Button variant="link" onClick={goBack}>
          {t('backToAccounting')}
        </Button>
      </div>
    );
  }

  const typeColors: Record<string, string> = {
    ASSET: 'bg-blue-100 text-blue-700',
    LIABILITY: 'bg-red-100 text-red-700',
    EQUITY: 'bg-purple-100 text-purple-700',
    INCOME: 'bg-green-100 text-green-700',
    EXPENSE: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{account.code}</h1>
              <span className={`px-2 py-0.5 text-xs font-medium ${typeColors[account.type]}`}>
                {account.type}
              </span>
              {!account.isActive && (
                <span className="bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {t('accountDetail.inactive')}
                </span>
              )}
              {account.isSystemAccount && (
                <span className="bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                  {t('accountDetail.system')}
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-lg">{account.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8"
            onClick={handleToggleActive}
            disabled={updateMutation.isPending}
          >
            {account.isActive ? (
              <>
                <ToggleRight className="mr-2 h-4 w-4" />
                {t('accountDetail.deactivate')}
              </>
            ) : (
              <>
                <ToggleLeft className="mr-2 h-4 w-4" />
                {t('accountDetail.activate')}
              </>
            )}
          </Button>
          <Button variant="outline" className="h-8" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            {t('accountDetail.edit')}
          </Button>
          {canDelete && (
            <Button
              variant="destructive"
              className="h-8"
              onClick={() => {
                deleteMutation.reset();
                setIsDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('accountDetail.delete')}
            </Button>
          )}
        </div>
      </div>

      {/* Account Details */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Info Card */}
        <div className="border-border bg-card border p-6">
          <h3 className="mb-4 text-lg font-semibold">{t('accountDetail.title')}</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.code')}</dt>
              <dd className="font-mono font-medium">{account.code}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.type')}</dt>
              <dd>{account.type}</dd>
            </div>
            {account.subType && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('accountDetail.subType')}</dt>
                <dd>{account.subType.replace(/_/g, ' ')}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.normalBalance')}</dt>
              <dd>{account.normalBalance}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.currency')}</dt>
              <dd>{account.currency}</dd>
            </div>
            {account.parent && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('accountDetail.parent')}</dt>
                <dd>
                  <Link
                    to={`/travel/accounting/accounts/${account.parent.id}`}
                    className="text-primary hover:underline"
                  >
                    {account.parent.code} - {account.parent.name}
                  </Link>
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.journalEntries')}</dt>
              <dd>{account._count.journalLines}</dd>
            </div>
          </dl>
          {account.description && (
            <div className="mt-4 border-t pt-4">
              <p className="text-muted-foreground text-sm">{account.description}</p>
            </div>
          )}
        </div>

        {/* Balance Card */}
        <div className="border-border bg-card border p-6">
          <h3 className="mb-4 text-lg font-semibold">{t('accountDetail.currentBalance')}</h3>
          <div className="flex items-center gap-3">
            <div className={`p-3 ${account.balance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {account.balance >= 0 ? (
                <TrendingUp
                  className={`h-6 w-6 ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                />
              ) : (
                <TrendingDown className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div>
              <p className={`text-3xl font-bold ${account.balance < 0 ? 'text-red-600' : ''}`}>
                {formatCurrency(account.balance, account.currency)}
              </p>
              <p className="text-muted-foreground text-sm">
                {account.normalBalance} balance account
              </p>
            </div>
          </div>
        </div>

        {/* Child Accounts */}
        {account.children.length > 0 && (
          <div className="border-border bg-card border p-6">
            <h3 className="mb-4 text-lg font-semibold">
              Child Accounts ({account.children.length})
            </h3>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {account.children.map((child) => (
                <Link
                  key={child.id}
                  to={`/travel/accounting/accounts/${child.id}`}
                  className="hover:bg-muted/50 flex items-center justify-between p-2 transition-colors"
                >
                  <div>
                    <span className="font-mono text-sm">{child.code}</span>
                    <span className="ml-2 text-sm">{child.name}</span>
                    {!child.isActive && (
                      <span className="text-muted-foreground ml-2 text-xs">(Inactive)</span>
                    )}
                  </div>
                  <span className={`font-mono text-sm ${child.balance < 0 ? 'text-red-600' : ''}`}>
                    {formatCurrency(child.balance, account.currency)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Ledger */}
      <div className="border-border bg-card border">
        <div className="border-b p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="text-muted-foreground h-5 w-5" />
              <h3 className="text-lg font-semibold">{t('accountDetail.accountLedger')}</h3>
            </div>
            <p className="text-muted-foreground text-sm">
              {ledgerData?.pagination.total || 0} {t('accountDetail.transactions')}
            </p>
          </div>
        </div>

        {isLedgerLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
          </div>
        ) : ledgerData?.entries.length === 0 ? (
          <div className="py-12 text-center">
            <FileText className="text-muted-foreground/50 mx-auto h-12 w-12" />
            <h3 className="mt-4 text-lg font-medium">{t('accountDetail.noTransactions')}</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              This account has no posted journal entries.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('accountDetail.date')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('accountDetail.entryNumber')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('accountDetail.description')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('accountDetail.debit')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('accountDetail.credit')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('accountDetail.balance')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ledgerData?.entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">
                        {new Date(entry.journalEntry.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/travel/accounting/journal-entries/${entry.journalEntry.id}`}
                          className="text-primary font-mono text-sm hover:underline"
                        >
                          {entry.journalEntry.entryNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm">
                          {entry.description || entry.journalEntry.description}
                        </p>
                        {(entry.customer || entry.partner) && (
                          <p className="text-muted-foreground text-xs">
                            {entry.customer
                              ? `${entry.customer.firstName} ${entry.customer.lastName}`
                              : entry.partner?.companyName}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {entry.debit > 0 ? formatCurrency(entry.debit, account.currency) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {entry.credit > 0 ? formatCurrency(entry.credit, account.currency) : '-'}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-mono text-sm font-medium ${entry.runningBalance < 0 ? 'text-red-600' : ''}`}
                      >
                        {formatCurrency(entry.runningBalance, account.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {ledgerData && ledgerData.pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 border-t p-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setLedgerPage((p) => Math.max(1, p - 1))}
                  disabled={ledgerPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('pagination.previous')}
                </Button>
                <span className="px-4 py-2 text-sm">
                  {t('pagination.page', {
                    current: ledgerPage,
                    total: ledgerData.pagination.totalPages,
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() =>
                    setLedgerPage((p) => Math.min(ledgerData.pagination.totalPages, p + 1))
                  }
                  disabled={ledgerPage >= ledgerData.pagination.totalPages}
                >
                  {t('pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      <AccountModal open={isEditModalOpen} onOpenChange={setIsEditModalOpen} account={account} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('accountDetail.deleteAccount')}</DialogTitle>
            <DialogDescription>
              {t('detail.deleteMessage', { code: `${account.code} - ${account.name}` })}
            </DialogDescription>
          </DialogHeader>
          <ChartDeleteOptions
            unlocked={unlocked}
            hasChildren={account.children.length > 0}
            hasPostings={account._count.journalLines > 0}
            cascade={cascadeDelete}
            onCascadeChange={setCascadeDelete}
            mergeTargetId={mergeTargetId}
            onMergeTargetChange={setMergeTargetId}
            options={(mergeCandidates?.accounts ?? [])
              // Leaves only: balances and postings never sit on a header account.
              .filter((candidate) => candidate.id !== account.id && candidate._count.children === 0)
              .map((candidate) => ({
                id: candidate.id,
                code: candidate.code,
                name: candidate.name,
              }))}
          />
          {deleteMutation.error && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {deleteMutation.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsDeleteDialogOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('accountDetail.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
